import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Copy, ExternalLink, MessageSquare } from 'lucide-react';
import { usePublicEventPages } from '@/hooks/usePublicEventPages';
import { ImageUploader } from '@/components/ImageUploader';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { generatePublicEventUrl } from '@/lib/urlUtils';
import { format } from 'date-fns';

const PublicEventAdmin: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { getPublicPage, updatePublicPage, loading } = usePublicEventPages();
  const { toast } = useToast();
  
  const [eventData, setEventData] = useState<any>(null);
  const [formData, setFormData] = useState({
    public_title: '',
    public_description: '',
    public_location: '',
    public_start_at: '',
    public_end_at: '',
    hero_image_url: '',
    rsvp_enabled: true
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (slug) {
      loadEventData();
    }
  }, [slug]);

  const loadEventData = async () => {
    if (!slug) return;
    
    const data = await getPublicPage(slug);
    if (data) {
      setEventData(data);
      setFormData({
        public_title: data.public_title || '',
        public_description: data.public_description || '',
        public_location: data.public_location || '',
        public_start_at: data.public_start_at ? format(new Date(data.public_start_at), "yyyy-MM-dd'T'HH:mm") : '',
        public_end_at: data.public_end_at ? format(new Date(data.public_end_at), "yyyy-MM-dd'T'HH:mm") : '',
        hero_image_url: data.hero_image_url || '',
        rsvp_enabled: data.rsvp_enabled !== false
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug) return;

    setSubmitting(true);
    const success = await updatePublicPage(slug, {
      ...formData,
      public_start_at: formData.public_start_at ? new Date(formData.public_start_at).toISOString() : undefined,
      public_end_at: formData.public_end_at ? new Date(formData.public_end_at).toISOString() : undefined
    });
    
    if (success) {
      await loadEventData();
      toast({
        title: "Modifications enregistrées",
        description: "La page publique a été mise à jour.",
      });
      if (eventData?.event_id) {
        navigate(`/event/${eventData.event_id}`);
      }
    }
    setSubmitting(false);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copié !' });
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const shareOnWhatsApp = (url: string) => {
    const message = encodeURIComponent(`Vous êtes invité(e) à cet événement ! Confirmez votre présence : ${url}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const publicPageUrl = slug ? generatePublicEventUrl(slug) : null;

  // Silent loading, show content immediately when available
  if (!eventData && !loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex items-center px-4 pt-2 pb-4 bg-background">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate("/")}
            className="hover:bg-transparent border-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold ml-3">Page introuvable</h1>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">Page introuvable</h2>
            <p className="text-muted-foreground mb-4">Cette page publique n'existe pas ou a été supprimée.</p>
            <Button onClick={() => navigate('/calendar')}>
              Retour au calendrier
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex items-center px-4 pt-2 pb-4 bg-background">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => eventData?.event_id ? navigate(`/event/${eventData.event_id}`) : navigate("/")}
          className="hover:bg-transparent border-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-0"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-2 ml-3 flex-1">
          <h1 className="text-lg font-semibold">{formData.public_title || eventData?.public_title || 'Événement'}</h1>
          <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">Page partagée</Badge>
        </div>
      </div>

      <div className="flex-1 px-4 pt-2 pb-32">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <ImageUploader
              currentImageUrl={formData.hero_image_url}
              onImageUploaded={(url) => handleInputChange('hero_image_url', url)}
              bucketPath="public-events"
              size="banner"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="public_title">Titre public</Label>
            <Input
              id="public_title"
              value={formData.public_title}
              onChange={(e) => handleInputChange('public_title', e.target.value)}
              placeholder="Titre qui sera affiché publiquement"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="public_description">Description publique</Label>
            <Textarea
              id="public_description"
              value={formData.public_description}
              onChange={(e) => handleInputChange('public_description', e.target.value)}
              placeholder="Description qui sera affichée publiquement"
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="public_location">Lieu public</Label>
            <Input
              id="public_location"
              value={formData.public_location}
              onChange={(e) => handleInputChange('public_location', e.target.value)}
              placeholder="Lieu qui sera affiché publiquement"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="public_start_at">Date et heure de début</Label>
            <Input
              id="public_start_at"
              type="datetime-local"
              value={formData.public_start_at}
              onChange={(e) => handleInputChange('public_start_at', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="public_end_at">Date et heure de fin</Label>
            <Input
              id="public_end_at"
              type="datetime-local"
              value={formData.public_end_at}
              onChange={(e) => handleInputChange('public_end_at', e.target.value)}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="rsvp_enabled"
              checked={formData.rsvp_enabled}
              onCheckedChange={(checked) => handleInputChange('rsvp_enabled', checked)}
            />
            <Label htmlFor="rsvp_enabled">Autoriser les réponses RSVP</Label>
          </div>

          {/* Bloc de partage */}
          <div className="pt-4 border-t">
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Créez une page publique pour permettre aux invités de confirmer leur présence et partager leurs coordonnées
              </p>
              
              {publicPageUrl && (
                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <Input value={publicPageUrl} readOnly className="flex-1" />
                    <Button variant="outline" size="icon" onClick={() => copyToClipboard(publicPageUrl)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" className="flex-1" onClick={() => window.open(publicPageUrl, '_blank')}>
                      <ExternalLink className="h-4 w-4 mr-2" /> Voir la page
                    </Button>
                    <Button variant="outline" className="flex-1" onClick={() => shareOnWhatsApp(publicPageUrl)}>
                      <MessageSquare className="h-4 w-4 mr-2" /> WhatsApp
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-4 border-t bg-background space-y-2 z-50">
        <Button 
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
        >
          {submitting ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </Button>
        <Button 
          variant="cancel-hover"
          onClick={() => eventData?.event_id ? navigate(`/event/${eventData.event_id}`) : navigate("/")}
          className="w-full"
        >
          Annuler
        </Button>
      </div>
    </div>
  );
};

export default PublicEventAdmin;